import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './AdminReportes.css';

const AdminReportes = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [rango, setRango] = useState('mes'); 
    const [desde, setDesde] = useState('');
    const [hasta, setHasta] = useState('');

    const [resumen, setResumen] = useState({
        totalTurnos: 0, confirmados: 0, pendientes: 0, cancelados: 0, finalizados: 0,
        totalPacientes: 0, totalMasajistas: 0, montoRecaudado: 0, montoPendiente: 0,
    });

    const [porMasajista, setPorMasajista] = useState([]);
    const [porDia, setPorDia] = useState([]);

    const calcularRango = () => {
        const formatearDateAStr = (date) => {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        };

        const hoy = new Date();
        let desdeCalc = new Date();

        if (rango === 'hoy') {
            // desdeCalc ya es hoy
        } else if (rango === 'semana') {
            desdeCalc.setDate(desdeCalc.getDate() - 6);
        } else if (rango === 'mes') {
            desdeCalc.setDate(desdeCalc.getDate() - 29);
        } else if (rango === 'custom') {
            if (!desde || !hasta) return null;
            return { desde: desde, hasta: hasta };
        }

        return { 
            desde: formatearDateAStr(desdeCalc), 
            hasta: formatearDateAStr(hoy) 
        };
    };

    useEffect(() => {
        const fetchReportes = async () => {
            const r = calcularRango();
            if (!r) return;

            try {
                setLoading(true);
                setError('');

                const params = { desde: r.desde, hasta: r.hasta };

                const [resumenRes, porMasajistaRes, porDiaRes] = await Promise.all([
                    api.get('/admin/reportes/resumen', { params }),
                    api.get('/admin/reportes/por-masajista', { params }),
                    api.get('/admin/reportes/por-dia', { params }),
                ]);

                setResumen(resumenRes.data);
                setPorMasajista(porMasajistaRes.data);
                setPorDia(porDiaRes.data);
            } catch (err) {
                setError(err.response?.data?.message || 'Error al cargar reportes');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchReportes();
    }, [rango, desde, hasta]);

    const formatearMoneda = (monto) => {
        return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 0 }).format(monto || 0);
    };

    const formatearFecha = (fecha) => {
        const [y, m, d] = fecha.split('-');
        return `${d}/${m}`;
    };

    const maxTurnosDia = Math.max(...porDia.map(d => d.total), 1);

    return (
        <div className="admin-reportes">
            <div className="admin-page-header" style={{ marginBottom: '1.5rem' }}>
                <h1>📊 Reportes y Estadísticas</h1>
                <p className="admin-page-subtitle">Resumen de actividad, ingresos y rendimiento</p>
            </div>

            <div className="reportes-filtros">
                <div className="toggle-group">
                    <button className={`toggle-btn ${rango === 'hoy' ? 'active' : ''}`} onClick={() => setRango('hoy')}>Hoy</button>
                    <button className={`toggle-btn ${rango === 'semana' ? 'active' : ''}`} onClick={() => setRango('semana')}>Últimos 7 días</button>
                    <button className={`toggle-btn ${rango === 'mes' ? 'active' : ''}`} onClick={() => setRango('mes')}>Últimos 30 días</button>
                    <button className={`toggle-btn ${rango === 'custom' ? 'active' : ''}`} onClick={() => setRango('custom')}>Personalizado</button>
                </div>

                {rango === 'custom' && (
                    <div className="custom-rango">
                        <input type="date" className="form-control" value={desde} onChange={(e) => setDesde(e.target.value)} />
                        <span>hasta</span>
                        <input type="date" className="form-control" value={hasta} onChange={(e) => setHasta(e.target.value)} />
                    </div>
                )}
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {loading ? (
                <div className="reportes-loading">Cargando reportes...</div>
            ) : (
                <>
                    <div className="stats-compact">
                        <div className="stat-card-compact primary">
                            <div className="stat-icon">📋</div>
                            <div className="stat-content">
                                <div className="stat-value">{resumen.totalTurnos}</div>
                                <div className="stat-label">Turnos totales</div>
                            </div>
                        </div>
                        <div className="stat-card-compact success">
                            <div className="stat-icon">💰</div>
                            <div className="stat-content">
                                <div className="stat-value">{formatearMoneda(resumen.montoRecaudado)}</div>
                                <div className="stat-label">Recaudado</div>
                            </div>
                        </div>
                        <div className="stat-card-compact warning" style={{ borderLeftColor: '#ffc107' }}>
                            <div className="stat-icon">⏳</div>
                            <div className="stat-content">
                                <div className="stat-value">{formatearMoneda(resumen.montoPendiente)}</div>
                                <div className="stat-label">Pendiente de cobro</div>
                            </div>
                        </div>
                        <div className="stat-card-compact info">
                            <div className="stat-icon">👥</div>
                            <div className="stat-content">
                                <div className="stat-value">{resumen.totalPacientes}</div>
                                <div className="stat-label">Pacientes activos</div>
                            </div>
                        </div>
                    </div>

                    <div className="reportes-estados">
                        <h3>Turnos por estado</h3>
                        <div className="estados-grid">
                            <div className="estado-item estado-confirmado">
                                <span className="estado-label">✅ Confirmados</span>
                                <span className="estado-value">{resumen.confirmados}</span>
                            </div>
                            <div className="estado-item estado-pendiente">
                                <span className="estado-label">⏳ Pendientes</span>
                                <span className="estado-value">{resumen.pendientes}</span>
                            </div>
                            <div className="estado-item estado-finalizado">
                                <span className="estado-label">✔️ Finalizados</span>
                                <span className="estado-value">{resumen.finalizados}</span>
                            </div>
                            <div className="estado-item estado-cancelado">
                                <span className="estado-label">❌ Cancelados</span>
                                <span className="estado-value">{resumen.cancelados}</span>
                            </div>
                        </div>
                    </div>

                    <div className="reportes-card">
                        <h3>Turnos por día</h3>
                        {porDia.length === 0 ? (
                            <div className="reportes-empty">No hay datos en este período</div>
                        ) : (
                            <div className="grafico-barras">
                                {porDia.map((d) => (
                                    <div key={d.fecha} className="barra-item">
                                        <div className="barra-wrapper">
                                            <div className="barra" style={{ height: `${(d.total / maxTurnosDia) * 100}%` }} title={`${d.total} turnos`}>
                                                <span className="barra-valor">{d.total}</span>
                                            </div>
                                        </div>
                                        <div className="barra-label">{formatearFecha(d.fecha)}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="reportes-card">
                        <h3>Rendimiento por masajista</h3>
                        {porMasajista.length === 0 ? (
                            <div className="reportes-empty">No hay datos en este período</div>
                        ) : (
                            <div className="tabla-reportes">
                                <div className="tabla-header">
                                    <div className="col-masajista">Masajista</div>
                                    <div className="col-num">Turnos</div>
                                    <div className="col-num">Confirmados</div>
                                    <div className="col-num">Cancelados</div>
                                    <div className="col-monto">Recaudado</div>
                                </div>
                                {porMasajista.map((m) => (
                                    <div key={m.masajistaId} className="tabla-row">
                                        <div className="col-masajista">{m.nombre} {m.apellido}</div>
                                        <div className="col-num">{m.totalTurnos}</div>
                                        <div className="col-num">{m.confirmados}</div>
                                        <div className="col-num">{m.cancelados}</div>
                                        <div className="col-monto">{formatearMoneda(m.montoRecaudado)}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminReportes;